import { useFeatureValue } from '@growthbook/growthbook-react';
import { Close } from '@mui/icons-material';
import {
  Box,
  Collapse,
  Fade,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { isBefore, subDays } from 'date-fns';
import { useState } from 'react';

import { ComplianceTypes } from '@/constants';
import { useDebouncedValue } from '@/hooks';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS } from '@/services';
import {
  selectIsGetStartedSectionDismissed,
  selectUserCompliance,
  selectUserProfileResult,
  useAppDispatch,
  useAppSelector,
  useFetchEpisodesQuery,
  userPreferencesActions,
} from '@/store';
import { OnboardingExperienceFlag } from '@/types';

import { DesktopEpisodes, MobileEpisodeSlideshow } from './components';

const carouselKey = 'Get Started';
const referrer = {
  page: EVENT_CONSTANTS.PAGE.DISCOVER,
  carousel: carouselKey,
};

export const YourFirstListenCarousel = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const compliance = useAppSelector(selectUserCompliance);
  const isPreviouslyDismissed = useAppSelector(
    selectIsGetStartedSectionDismissed,
  );
  const { data: profile } = useAppSelector(selectUserProfileResult);

  const [isOpen, setIsOpen] = useState(true);

  const { experience: onboardingExperience, episodes: onboardingEpisodes } =
    useFeatureValue<OnboardingExperienceFlag>('onboarding-experience', {});
  const isFeatureEnabled = onboardingExperience === 'recommended-episodes';

  const episodeIds = onboardingEpisodes?.split('|') ?? [];
  const skipPredicate =
    useDebouncedValue(
      isPreviouslyDismissed ||
        !profile?.onboardingCompletedAt ||
        isBefore(
          new Date(profile.onboardingCompletedAt),
          subDays(new Date(), 14),
        ) ||
        episodeIds.length === 0 ||
        !isFeatureEnabled,
      2000,
    ) ?? true;

  // prevent layout shift issues caused by animating Collapse and Fade simultaneously
  // default initial undefined value to true
  const delayedSkipPredicate = useDebouncedValue(skipPredicate, 500) ?? true;

  const { data: episodes, isLoading } = useFetchEpisodesQuery(
    {
      episodeIds,
      compliance,
    },
    { skip: skipPredicate },
  );

  // TODO: Need to have this empty when not shown, to avoid inserting flex gap, but unsure if this will fade out as expected
  if (skipPredicate) return null;

  const onClose = () => {
    dispatch(userPreferencesActions.dismissGetStartedSection());
    setIsOpen(false); // for immediate removal from layout
    AnalyticsService.logEvent(EVENTS.CAROUSEL_EVENTS.DISMISSED, {
      carousel: carouselKey,
    });
  };

  return (
    <Collapse in={!skipPredicate && isOpen} timeout={500}>
      <Box
        sx={{
          p: 3,
          background: theme.palette.gradients.radial,
          borderRadius: 4,
          overflow: 'hidden',
          height: 400,
        }}>
        <Fade in={!delayedSkipPredicate && isOpen} timeout={500}>
          <Box
            sx={{
              display: 'flex',
              visibility: delayedSkipPredicate ? 'hidden' : 'visible',
              flexDirection: 'column',
              gap: 2,
            }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}>
              <Box>
                <Typography variant="h4">Get Started</Typography>
                <Typography
                  variant="body3"
                  sx={{ display: { xs: 'none', md: 'block' } }}>
                  Start earning {ComplianceTypes[compliance]} now, or bookmark
                  episodes for later
                </Typography>
              </Box>
              <IconButton
                aria-label="Close modal"
                onClick={onClose}
                sx={{
                  transform: `translateY(-${styles.iconButton.fontSize / 2}px) translateX(${styles.iconButton.fontSize / 2}px)`,
                }}>
                <Close sx={styles.iconButton} />
              </IconButton>
            </Box>
            {isMobile ? (
              <MobileEpisodeSlideshow
                compliance={compliance}
                episodes={episodes}
                isLoading={isLoading}
                referrer={referrer}
              />
            ) : (
              <DesktopEpisodes
                compliance={compliance}
                episodes={episodes}
                isLoading={isLoading}
                referrer={referrer}
              />
            )}
          </Box>
        </Fade>
      </Box>
    </Collapse>
  );
};

const styles = {
  iconButton: {
    fontSize: 24,
  },
};
